import { CmcWebApi } from '@cmctechnology/webinvest-api-client-web-trading';
import { FrontendLocations } from '@cmctechnology/webinvest-store-frontend';
import { configureStore, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IDividends,
  IDividendsResponse,
  IEsgDataResponse,
  IInstrumentDetail,
  INews,
  InstrumentClass,
  InstrumentCode,
  NewsIndicatorType,
  OptionStyle,
  OptionType
} from './types';
import { parseDateFromSydney } from './utils';

export const INSTRUMENT_STORE_NAME = `instrument`;

export type TiprankStatus = {
  isPending: boolean;
  isError: boolean;
  isSupported?: boolean;
};

export interface IInstrumentStore {
  details: Record<InstrumentCode, IInstrumentDetail>;
  logos: Record<InstrumentCode, string>;
  searchResults: Partial<Record<FrontendLocations, InstrumentCode[]>>;
  searchQuery: Partial<Record<FrontendLocations, string>>;
  dividends: Record<InstrumentCode, IDividends | undefined>;
  news: Record<InstrumentCode, INews | undefined>;
  esg: Record<InstrumentCode, IEsgDataResponse | undefined>;
  productSearchResult: Partial<Record<FrontendLocations, CmcWebApi.MarketInformation.ProductsSearch2ResultItem[]>>;
  tipRanks: Record<InstrumentCode, TiprankStatus>;
}

export const defaultInstrumentStore: IInstrumentStore = {
  details: {},
  logos: {},
  searchResults: {},
  searchQuery: {},
  dividends: {},
  news: {},
  esg: {},
  productSearchResult: {},
  tipRanks: {}
};

export const createInstrumentStore = (initialState: IInstrumentStore) =>
  createSlice({
    name: INSTRUMENT_STORE_NAME,
    initialState,
    reducers: {
      storeReset: () => defaultInstrumentStore,

      detailsFetched: (store, action: PayloadAction<CmcWebApi.MarketInformation.ProductStaticDataModel2[]>) => {
        action.payload.forEach((instrument) => {
          const detail = store.details[instrument.Code] ?? ({} as IInstrumentDetail);
          if (detail?.fullDetail) return;

          detail.fullDetail = true;
          detail.class = instrument.InstrumentClass as InstrumentClass;
          detail.instrument = instrument.Code;
          detail.companyName = instrument.CompanyName;
          detail.exchangeId = (instrument as unknown as { ExchangeId: string }).ExchangeId;
          detail.longName = instrument.LongDescription;
          detail.name = instrument.ShortDescription;
          detail.tradeable = instrument.IsTradeable;
          detail.underlyingCode = instrument.UnderlyingCode;
          detail.sector = instrument.EquitySector;

          detail.equity = {
            industryGroup: instrument.EquityIndustryGroup,
            sector: instrument.EquitySector
          };
          detail.irs = {
            couponFrequency: instrument.IrsCouponFrequency,
            couponDate: instrument.IrsCouponDate?.valueOf(),
            couponRate: instrument.IrsCouponRate,
            faceValue: instrument.IrsFaceValue,
            maturityDate: instrument.IrsMaturityDate?.valueOf()
          };
          detail.mfund = {
            issuer: instrument.MFundIssuer,
            launchDate: instrument.MFundLaunchDate?.valueOf(),
            pdsIssueDate: instrument.MFundPdsIssueDate?.valueOf(),
            pdsProfileUrl: instrument.MFundProfileUrl,
            pdsUrl: instrument.MFundPdsUrl
          };
          detail.option = {
            contractSize: instrument.OptionContractSize,
            expiryDate: instrument.OptionExpiryDate?.valueOf(),
            strikePrice: instrument.OptionStrikePrice,
            style: instrument.OptionStyle as OptionStyle,
            type: instrument.OptionType as OptionType
          };
          detail.warrant = {
            expiry: instrument.WarrantExpiry?.valueOf(),
            issuer: instrument.WarrantIssuer,
            series: instrument.WarrantSeries,
            strikePrice: instrument.WarrantStrikePrice,
            strikePriceUnit: instrument.WarrantStrikePriceUnit,
            type: instrument.WarrantType
          };
          store.details[instrument.Code] = detail;
        });
      },

      logoFetched: (store, action: PayloadAction<{ instrument: InstrumentCode; url: string }>) => {
        store.logos[action.payload.instrument] = action.payload.url;
      },

      querySearched: (store, action: PayloadAction<{ location: FrontendLocations; query: string }>) => {
        store.searchQuery[action.payload.location] = action.payload.query;
      },

      productSearchResults: (
        store,
        action: PayloadAction<{ location: FrontendLocations; results: CmcWebApi.MarketInformation.ProductStaticDataMinModel3[] }>
      ) => {
        store.searchResults[action.payload.location] = action.payload.results.map((result) => result.Code);
        action.payload.results.forEach((instrument) => {
          const detail: IInstrumentDetail = (store.details[instrument.Code] ??= {
            fullDetail: false,
            instrument: instrument.Code
          } as IInstrumentDetail);
          detail.class ??= instrument.InstrumentClass as InstrumentClass;
          detail.companyName ||= instrument.CompanyName;
          detail.exchangeId ??= (instrument as unknown as { ExchangeId: string }).ExchangeId;
          detail.tradeable ??= instrument.IsTradeable;
          detail.underlyingCode ??= instrument.UnderlyingCode;
          detail.country ??= instrument.CountryIsoCode;
        });
      },

      tiprankFetched: (store, action: PayloadAction<{ instrument: InstrumentCode; status: TiprankStatus }>) => {
        const { instrument, status } = action.payload;
        store.tipRanks[instrument] = status;
      },

      tipRankSupportPending: (store, action: PayloadAction<{ instrument: InstrumentCode }>) => {
        const { instrument } = action.payload;
        store.tipRanks[instrument] = { isPending: true, isError: false };
      },

      dividendFetched: (store, action: PayloadAction<{ fetched: InstrumentCode[]; response: IDividendsResponse[] }>) => {
        action.payload.response.forEach((dividend) => {
          store.dividends[dividend['ASX Code']] = {
            instrument: dividend['ASX Code'],
            companyName: dividend['Company Name'],
            exDividendDate: parseDateFromSydney(dividend['Ex Dividend Date']!, 'DD MMM, YY').valueOf(),
            dividendPayDate: parseDateFromSydney(dividend['Dividend Pay Date']!, 'DD MMM, YY').valueOf(),
            amount: Number(dividend.Amount),
            franking: Number(dividend.Franking?.replaceAll('%', ''))
          };
        });

        action.payload.fetched.forEach((stockCode) => {
          // Note: store fetched codes to prevent from re-fetching on new call
          store.dividends[stockCode] = store.dividends[stockCode] ?? undefined;
        });
      },

      newsFetched: (store, action: PayloadAction<CmcWebApi.MarketInformation.NewsIndicatorBatchModel1>) => {
        action.payload.NewsIndicators.forEach((indicator) => {
          store.news[indicator.Code] = {
            instrument: indicator.Code,
            indicatorType: indicator.IndicatorType as NewsIndicatorType,
            heading: indicator.Heading,
            releaseDate: parseDateFromSydney(indicator.ReleaseDate as unknown as string).valueOf()
          };
        });

        action.payload.CodesNoData.forEach((instrument) => {
          // Note: store fetched codes to prevent from re-fetching on new call
          store.news[instrument] = undefined;
        });
      },

      exchangeFetched: (store, action: PayloadAction<{ instrument: string; exchangeId: string }[]>) => {
        action.payload.forEach((instrument) => {
          const detail: IInstrumentDetail = (store.details[instrument.instrument] ??= {
            fullDetail: false,
            instrument: instrument.instrument
          } as IInstrumentDetail);
          detail.exchangeId ??= instrument.exchangeId;
        });
      },

      esgFetched: (store, action: PayloadAction<{ instrument: InstrumentCode; response: IEsgDataResponse }>) => {
        const { instrument, response } = action.payload;
        store.esg[instrument] = response;
      },

      productLibrarySearchResult: (
        store,
        action: PayloadAction<{ location: FrontendLocations; result: CmcWebApi.MarketInformation.ProductsSearch2PaginatedResult }>
      ) => {
        const items = action.payload.result.Items;
        store.productSearchResult[action.payload.location] = items;
      }
    }
  });

export const { actions, reducer } = createInstrumentStore(defaultInstrumentStore);

export const createStore = (initialStore = defaultInstrumentStore) =>
  configureStore({ reducer: { [INSTRUMENT_STORE_NAME]: createInstrumentStore(initialStore).reducer } });

export const InstrumentStore = createStore();
export type IStore = ReturnType<typeof InstrumentStore.getState>;
